import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, Clock, Globe, BarChart3, ChevronDown, ChevronUp } from "lucide-react";

interface TimePickerProps {
    dates: {
        from: string;
        to: string;
    };
    onDatesChange: (dates: { from: string; to: string }) => void;
    onRefresh: () => void;
    isLoading: boolean;
}

interface RelativeTimeOption {
    label: string;
    value: string;
    getDates: () => { from: string; to: string };
}

const AdvancedTimePicker: React.FC<TimePickerProps> = ({ dates, onDatesChange, onRefresh, isLoading }) => {
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [activeTab, setActiveTab] = useState<"relative" | "absolute" | "startend">("relative");
    const [relativeTimeInput, setRelativeTimeInput] = useState("");
    const [selectedRelative, setSelectedRelative] = useState<string>("");

    // Absolute time states
    const [absoluteDate, setAbsoluteDate] = useState<Date | undefined>(new Date());
    const [absoluteTime, setAbsoluteTime] = useState("20:01:10");
    const [duration, setDuration] = useState("± 1 hour");
    const [absoluteDateOpen, setAbsoluteDateOpen] = useState(false);

    // Start/End time states
    const [startDate, setStartDate] = useState<Date | undefined>(new Date());
    const [startTime, setStartTime] = useState("07:01:10");
    const [endDate, setEndDate] = useState<Date | undefined>(new Date());
    const [endTime, setEndTime] = useState("21:01:10");
    const [startDateOpen, setStartDateOpen] = useState(false);
    const [endDateOpen, setEndDateOpen] = useState(false);

    const [timeZone] = useState("AEST (UTC+10)");

    const relativeOptions: RelativeTimeOption[] = [
        {
            label: "Today",
            value: "today",
            getDates: () => {
                const today = new Date();
                const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
                return {
                    from: startOfDay.toISOString(),
                    to: today.toISOString(),
                };
            },
        },
        {
            label: "Yesterday",
            value: "yesterday",
            getDates: () => {
                const today = new Date();
                const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
                const startOfYesterday = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());
                const endOfYesterday = new Date(startOfYesterday.getTime() + 24 * 60 * 60 * 1000 - 1);
                return {
                    from: startOfYesterday.toISOString(),
                    to: endOfYesterday.toISOString(),
                };
            },
        },
        {
            label: "Last 15 seconds",
            value: "15s",
            getDates: () => {
                const now = new Date();
                const from = new Date(now.getTime() - 15 * 1000);
                return { from: from.toISOString(), to: now.toISOString() };
            },
        },
        {
            label: "Last 30 seconds",
            value: "30s",
            getDates: () => {
                const now = new Date();
                const from = new Date(now.getTime() - 30 * 1000);
                return { from: from.toISOString(), to: now.toISOString() };
            },
        },
        {
            label: "Last 1 minute",
            value: "1m",
            getDates: () => {
                const now = new Date();
                const from = new Date(now.getTime() - 60 * 1000);
                return { from: from.toISOString(), to: now.toISOString() };
            },
        },
        {
            label: "Last 15 minutes",
            value: "15m",
            getDates: () => {
                const now = new Date();
                const from = new Date(now.getTime() - 15 * 60 * 1000);
                return { from: from.toISOString(), to: now.toISOString() };
            },
        },
        {
            label: "Last 1 hour",
            value: "1h",
            getDates: () => {
                const now = new Date();
                const from = new Date(now.getTime() - 60 * 60 * 1000);
                return { from: from.toISOString(), to: now.toISOString() };
            },
        },
        {
            label: "Last 24 hours",
            value: "24h",
            getDates: () => {
                const now = new Date();
                const from = new Date(now.getTime() - 24 * 60 * 60 * 1000);
                return { from: from.toISOString(), to: now.toISOString() };
            },
        },
        {
            label: "Last 7 days",
            value: "7d",
            getDates: () => {
                const now = new Date();
                const from = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                return { from: from.toISOString(), to: now.toISOString() };
            },
        },
        {
            label: "Last 30 days",
            value: "30d",
            getDates: () => {
                const now = new Date();
                const from = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
                return { from: from.toISOString(), to: now.toISOString() };
            },
        },
    ];

    const handleRelativeSelection = (option: RelativeTimeOption) => {
        setSelectedRelative(option.value);
        setRelativeTimeInput(option.label);
        const newDates = option.getDates();
        onDatesChange(newDates);
    };

    const handleCustomRelativeTime = (input: string) => {
        setRelativeTimeInput(input);
        setSelectedRelative("custom");

        const match = input.match(/^(\d+)([smhd])$/i);
        if (match) {
            const [, amount, unit] = match;
            const now = new Date();
            let milliseconds = 0;

            switch (unit.toLowerCase()) {
                case "s":
                    milliseconds = parseInt(amount) * 1000;
                    break;
                case "m":
                    milliseconds = parseInt(amount) * 60 * 1000;
                    break;
                case "h":
                    milliseconds = parseInt(amount) * 60 * 60 * 1000;
                    break;
                case "d":
                    milliseconds = parseInt(amount) * 24 * 60 * 60 * 1000;
                    break;
            }

            const from = new Date(now.getTime() - milliseconds);
            onDatesChange({
                from: from.toISOString(),
                to: now.toISOString(),
            });
        }
    };

    const combineDateTime = (date: Date | undefined, time: string): Date => {
        if (!date) return new Date();

        const [hours, minutes, seconds] = time.split(":").map(Number);
        const combined = new Date(date);
        combined.setHours(hours || 0, minutes || 0, seconds || 0, 0);
        return combined;
    };

    const handleAbsoluteTimeChange = () => {
        if (!absoluteDate) return;

        try {
            const centerTime = combineDateTime(absoluteDate, absoluteTime);

            const durationMatch = duration.match(/± (\d+) (\w+)/);
            if (durationMatch) {
                const [, amount, unit] = durationMatch;
                let milliseconds = 0;

                switch (unit) {
                    case "minute":
                    case "minutes":
                        milliseconds = parseInt(amount) * 60 * 1000;
                        break;
                    case "hour":
                    case "hours":
                        milliseconds = parseInt(amount) * 60 * 60 * 1000;
                        break;
                    case "day":
                    case "days":
                        milliseconds = parseInt(amount) * 24 * 60 * 60 * 1000;
                        break;
                }

                const from = new Date(centerTime.getTime() - milliseconds);
                const to = new Date(centerTime.getTime() + milliseconds);

                onDatesChange({
                    from: from.toISOString(),
                    to: to.toISOString(),
                });
            }
        } catch (error) {
            console.error("Error parsing absolute time:", error);
        }
    };

    const handleStartEndTimeChange = () => {
        try {
            const fromDate = combineDateTime(startDate, startTime);
            const toDate = combineDateTime(endDate, endTime);

            onDatesChange({
                from: fromDate.toISOString(),
                to: toDate.toISOString(),
            });
        } catch (error) {
            console.error("Error parsing start/end time:", error);
        }
    };

    useEffect(() => {
        if (activeTab === "absolute") {
            handleAbsoluteTimeChange();
        } else if (activeTab === "startend") {
            handleStartEndTimeChange();
        }
    }, [absoluteDate, absoluteTime, duration, startDate, startTime, endDate, endTime, activeTab]);

    const DateTimePicker = ({ label, date, time, onDateChange, onTimeChange, dateOpen, setDateOpen }: { label: string; date: Date | undefined; time: string; onDateChange: (date: Date | undefined) => void; onTimeChange: (time: string) => void; dateOpen: boolean; setDateOpen: (open: boolean) => void }) => (
        <div className="space-y-4">
            <h3 className="text-sm font-medium text-foreground border-b border-border pb-2">{label}</h3>

            <div className="flex flex-col gap-2">
                <Label htmlFor={`${label.toLowerCase()}-date`} className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Date
                </Label>
                <Popover open={dateOpen} onOpenChange={setDateOpen}>
                    <PopoverTrigger asChild>
                        <Button variant="outline" id={`${label.toLowerCase()}-date`} className="w-full justify-between font-normal">
                            {date ? date.toLocaleDateString() : "Select date"}
                            <CalendarIcon className="w-4 h-4 text-muted-foreground" />
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto overflow-hidden p-0" align="start">
                        <Calendar
                            mode="single"
                            selected={date}
                            captionLayout="dropdown"
                            onSelect={(selectedDate) => {
                                onDateChange(selectedDate);
                                setDateOpen(false);
                            }}
                        />
                    </PopoverContent>
                </Popover>
            </div>

            <div className="flex flex-col gap-2">
                <Label htmlFor={`${label.toLowerCase()}-time`} className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Time
                </Label>
                <div className="flex items-center space-x-2">
                    <Input type="time" id={`${label.toLowerCase()}-time`} step="1" value={time} onChange={(e) => onTimeChange(e.target.value)} className="flex-1" />
                    <span className="text-xs text-muted-foreground whitespace-nowrap">{timeZone}</span>
                </div>
            </div>
        </div>
    );

    const tabItems = [
        { key: "relative" as const, label: "Quick Select", icon: BarChart3 },
        { key: "startend" as const, label: "Start & End", icon: CalendarIcon },
        { key: "absolute" as const, label: "Around a Time", icon: Clock },
    ];

    return (
        <div className="rounded-lg border bg-card mb-4">
            {/* Collapsible Header */}
            <div
                className="flex justify-between items-center px-6 py-4 cursor-pointer border-b"
                onClick={() => setIsCollapsed(!isCollapsed)}
            >
                <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                    Time Range
                </h3>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                            e.stopPropagation();
                            onRefresh();
                        }}
                        disabled={isLoading}
                    >
                        {isLoading ? "Loading..." : "Refresh Data"}
                    </Button>
                    {isCollapsed ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronUp className="w-4 h-4 text-muted-foreground" />}
                </div>
            </div>

            {/* Collapsible Content */}
            {!isCollapsed && (
                <div className="p-6">
                    {/* Tab Navigation */}
                    <nav className="flex items-center gap-1 rounded-lg bg-muted p-1 mb-4">
                        {tabItems.map((tab) => (
                            <button
                                key={tab.key}
                                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs sm:text-sm font-medium rounded-md transition-all duration-150 flex-1 justify-center
                                    ${activeTab === tab.key
                                        ? "bg-card text-foreground shadow-sm"
                                        : "text-muted-foreground hover:text-foreground"
                                    }`}
                                onClick={() => setActiveTab(tab.key)}
                            >
                                <tab.icon className="w-3.5 h-3.5" />
                                <span className="hidden sm:inline">{tab.label}</span>
                            </button>
                        ))}
                    </nav>

                    {activeTab === "relative" && (
                        <div className="space-y-4">
                            <div>
                                <Input
                                    type="text"
                                    placeholder="Relative time (15m, 1h, 1d, 1w)"
                                    value={relativeTimeInput}
                                    onChange={(e) => setRelativeTimeInput(e.target.value)}
                                    onKeyPress={(e) => {
                                        if (e.key === "Enter") {
                                            handleCustomRelativeTime(relativeTimeInput);
                                        }
                                    }}
                                />
                            </div>

                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                                {relativeOptions.map((option) => (
                                    <Button
                                        key={option.value}
                                        variant={selectedRelative === option.value ? "default" : "outline"}
                                        size="sm"
                                        className="flex justify-between items-center h-auto py-2.5"
                                        onClick={() => handleRelativeSelection(option)}
                                    >
                                        <span className="text-xs">{option.label}</span>
                                        <span className="text-xs opacity-50 font-mono">{option.value}</span>
                                    </Button>
                                ))}
                            </div>
                        </div>
                    )}

                    {activeTab === "startend" && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <DateTimePicker label="Start Time" date={startDate} time={startTime} onDateChange={setStartDate} onTimeChange={setStartTime} dateOpen={startDateOpen} setDateOpen={setStartDateOpen} />
                                <DateTimePicker label="End Time" date={endDate} time={endTime} onDateChange={setEndDate} onTimeChange={setEndTime} dateOpen={endDateOpen} setDateOpen={setEndDateOpen} />
                            </div>

                            <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted p-3 rounded-md">
                                <Globe className="w-3.5 h-3.5 flex-shrink-0" />
                                <span><span className="font-medium">Timezone:</span> {timeZone}</span>
                            </div>
                        </div>
                    )}

                    {activeTab === "absolute" && (
                        <div className="space-y-4">
                            <div className="flex flex-col gap-2">
                                <Label htmlFor="absolute-date" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                    Date
                                </Label>
                                <Popover open={absoluteDateOpen} onOpenChange={setAbsoluteDateOpen}>
                                    <PopoverTrigger asChild>
                                        <Button variant="outline" id="absolute-date" className="w-full justify-between font-normal">
                                            {absoluteDate ? absoluteDate.toLocaleDateString() : "Select date"}
                                            <CalendarIcon className="w-4 h-4 text-muted-foreground" />
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto overflow-hidden p-0" align="start">
                                        <Calendar
                                            mode="single"
                                            selected={absoluteDate}
                                            captionLayout="dropdown"
                                            onSelect={(date) => {
                                                setAbsoluteDate(date);
                                                setAbsoluteDateOpen(false);
                                            }}
                                        />
                                    </PopoverContent>
                                </Popover>
                            </div>

                            <div className="flex flex-col gap-2">
                                <Label htmlFor="absolute-time" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                    Time
                                </Label>
                                <div className="flex items-center space-x-2">
                                    <Input type="time" id="absolute-time" step="1" value={absoluteTime} onChange={(e) => setAbsoluteTime(e.target.value)} className="flex-1" />
                                    <span className="text-xs text-muted-foreground whitespace-nowrap">{timeZone}</span>
                                </div>
                            </div>

                            <div className="flex flex-col gap-2">
                                <Label htmlFor="duration" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                    Duration
                                </Label>
                                <select id="duration" className="w-full px-3 py-2 text-sm border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2" value={duration} onChange={(e) => setDuration(e.target.value)}>
                                    <option value="± 15 minutes">± 15 minutes</option>
                                    <option value="± 30 minutes">± 30 minutes</option>
                                    <option value="± 1 hour">± 1 hour</option>
                                    <option value="± 2 hours">± 2 hours</option>
                                    <option value="± 6 hours">± 6 hours</option>
                                    <option value="± 12 hours">± 12 hours</option>
                                    <option value="± 1 day">± 1 day</option>
                                </select>
                            </div>

                            <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted p-3 rounded-md">
                                <Globe className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                                <div>
                                    <p><span className="font-medium">Timezone:</span> {timeZone}</p>
                                    <p className="mt-1">Select a center point in time and choose how far to look before and after.</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Current Selection Display */}
                    <div className="mt-4 pt-4 border-t border-border">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 text-xs text-muted-foreground">
                            <span className="font-medium">Current selection:</span>
                            <span className="font-mono">
                                {new Date(dates.from).toLocaleString()} — {new Date(dates.to).toLocaleString()}
                            </span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdvancedTimePicker;
